import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CardContent,
  Typography,
  Chip,
  Grid,
  Paper,
  Avatar,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import HistoryToggleOffRoundedIcon from "@mui/icons-material/HistoryToggleOffRounded";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import themeCreator from "@Themes/themeCreator";
import useGetItem from "@Hooks/useGetItem";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import PageTitle from "@Components/PageTitle/PageTitle";
import { StyledCard, SectionTitle, ValueText } from "@Pages/RegulatoryMember/ExitRequestDetail/styles";

const getDelegationStatusColor = (status) => {
  switch (status) {
    case "ACCEPTED":
      return "success";
    case "REJECTED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const InfoField = ({ label, value, valueSx = {}, fullWidth = false }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6}>
    <Typography
      variant="body2"
      color="textSecondary"
      sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0.25 }}
    >
      {label}
    </Typography>
    {typeof value === "string" || typeof value === "number" ? (
      <ValueText variant="body1" sx={{ fontSize: "0.95rem", ...valueSx }}>
        {value ?? "-"}
      </ValueText>
    ) : (
      value ?? <ValueText variant="body1" sx={{ fontSize: "0.95rem" }}>-</ValueText>
    )}
  </Grid>
);

const DelegateRequestDetails = ({ requestData: propRequestData, onBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { delegateId } = useParams();
  const getItem = useGetItem();

  const [requestData, setRequestData] = useState(propRequestData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (!propRequestData && delegateId) {
      setLoading(true);
      getItem("delegation", delegateId)
        .then((data) => {
          if (data) {
            setRequestData(data);
          } else {
            setError("Delegation request not found");
          }
        })
        .catch((err) => {
          console.error("Failed to fetch delegation request:", err);
          setError("Failed to load delegation request details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [delegateId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return dayjs(timestamp).format("MMM DD, YYYY - HH:mm");
  };

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return "-";
    return dayjs(timestamp).format("MM/DD/YYYY");
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LoadingComponent size="5em" thickness={5} color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
          height: "91vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: "none" }}
          >
            Return
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!requestData) {
    return (
      <Box
        sx={{
          p: 2,
          height: "91vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: "none" }}
          >
            Return
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Typography variant="h6" color="textSecondary">
            No request data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const statusColor = getDelegationStatusColor(requestData.status);

  const renderContent = () => {
    if (tabValue === 0) {
      // Request Information Tab
      return (
        <Box sx={{ overflow: "auto", height: "100%", pr: 1, pt: 0 }}>
          <Grid container spacing={1.5}>
            {/* Ingredient Details */}
            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                  backgroundColor: "#fcfcfc",
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                }}
              >
                <CardContent sx={{ p: 1.5, pb: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: "rgba(15, 66, 170, 0.2)",
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Ingredient Details
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0.25 }}
                      >
                        Name
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          maxWidth: "100%",
                          display: "block",
                        }}
                      >
                        {requestData.exitRequestIngredientName}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0.25 }}
                      >
                        CAS Number
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          fontFamily: "monospace",
                          letterSpacing: "0.05em",
                          bgcolor: "white",
                          py: 0.25,
                          px: 1,
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          display: "inline-block",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {requestData.exitRequestIngredientCasNumber}
                      </ValueText>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Classification */}
            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Classification
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <InfoField
                      label="Region"
                      value={requestData.regionName}
                      valueSx={{ color: theme.palette.black.light }}
                    />
                    <InfoField
                      label="Category"
                      value={requestData.categoryName}
                      valueSx={{ color: theme.palette.black.light }}
                    />
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Request Information */}
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Request Information
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <InfoField
                      label="Request ID"
                      value={`#${requestData.id}`}
                      valueSx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      }}
                    />
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0.25 }}
                      >
                        Status
                      </Typography>
                      <Chip
                        label={requestData.status?.replace(/_/g, " ")}
                        color={statusColor}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: 600,
                        }}
                      />
                    </Grid>
                    <InfoField
                      label="Created At"
                      value={formatDateOnly(requestData.createdAt)}
                      valueSx={{
                        color: theme.palette.blue.main,
                        textDecoration: "underline",
                        textDecorationColor: "rgba(21, 155, 202, 0.3)",
                      }}
                    />
                    <InfoField
                      label="Responded At"
                      value={
                        requestData.respondedAt
                          ? formatDateOnly(requestData.respondedAt)
                          : "-"
                      }
                      valueSx={{
                        color: requestData.respondedAt
                          ? theme.palette.blue.main
                          : "inherit",
                      }}
                    />
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Linked Exit Request */}
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Linked Exit Request
                  </SectionTitle>
                  <Box
                    onClick={() =>
                      requestData.exitRequestId &&
                      navigate(
                        DefaultRoutes.reqInfo.replace(
                          ":reqId",
                          requestData.exitRequestId
                        )
                      )
                    }
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: requestData.exitRequestId
                        ? "pointer"
                        : "default",
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      bgcolor: "white",
                      transition: "0.2s",
                      "&:hover": requestData.exitRequestId
                        ? {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            borderColor: theme.palette.primary.main,
                          }
                        : {},
                    }}
                  >
                    <EventNoteIcon
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <ValueText
                      variant="body1"
                      sx={{
                        fontSize: "0.9rem",
                        color: requestData.exitRequestId
                          ? theme.palette.primary.main
                          : "textSecondary",
                        fontWeight: 600,
                      }}
                    >
                      {requestData.exitRequestId
                        ? `View Exit Request #${requestData.exitRequestId}`
                        : "No linked exit request"}
                    </ValueText>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      );
    }

    if (tabValue === 1) {
      // People Involved Tab
      return (
        <Box sx={{ overflow: "auto", height: "100%", pr: 1, pt: 1 }}>
          <Grid container spacing={2}>
            {/* Requester */}
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.info.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.info.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Requester
                  </SectionTitle>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.info.main,
                        width: 44,
                        height: 44,
                        mr: 1.5,
                        color: theme.palette.getContrastText(
                          theme.palette.info.main
                        ),
                      }}
                    >
                      <AccountCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {requestData.requesterUsername || "-"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {requestData.requesterEmail || ""}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Proposed Delegate */}
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.secondary.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                  borderLeft: `4px solid ${theme.palette.secondary.main}`,
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Proposed Delegate
                  </SectionTitle>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        width: 44,
                        height: 44,
                        mr: 1.5,
                        color: theme.palette.getContrastText(
                          theme.palette.secondary.main
                        ),
                      }}
                    >
                      <AccountCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {requestData.proposedDelegateUsername || "-"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {requestData.proposedDelegateEmail || ""}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      );
    }

    if (tabValue === 2) {
      // Messages & Timeline Tab
      return (
        <Box sx={{ overflow: "auto", height: "100%", pr: 1, pt: 1 }}>
          <Grid container spacing={2}>
            {/* Request Reason */}
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.info.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.info.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Request Reason
                  </SectionTitle>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: "rgba(255, 255, 255, 0.7)",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      maxHeight: "200px",
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontStyle: "italic",
                        color: theme.palette.black.light2,
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {requestData.requestReason || "No reason provided"}
                    </Typography>
                  </Paper>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Response Message */}
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                  backgroundColor: "#fcfcfc",
                  borderLeft: `4px solid ${
                    requestData.status === "REJECTED"
                      ? theme.palette.error.main
                      : requestData.status === "ACCEPTED"
                      ? theme.palette.success.main
                      : theme.palette.warning.main
                  }`,
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color:
                        requestData.status === "REJECTED"
                          ? theme.palette.error.main
                          : requestData.status === "ACCEPTED"
                          ? theme.palette.success.main
                          : theme.palette.warning.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Response Message
                  </SectionTitle>
                  {requestData.respondedAt && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {formatDate(requestData.respondedAt)}
                    </Typography>
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: "rgba(255, 255, 255, 0.7)",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      maxHeight: "200px",
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontStyle: "italic",
                        color: theme.palette.black.light2,
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {requestData.responseMessage || "No response yet"}
                    </Typography>
                  </Paper>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        pb: 0,
        pt: 3,
        pr: 2,
        pl: 2,
        height: "91vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <PageTitle
          title="Delegation Request Info"
          PageIcon={<FolderSpecialIcon color="white" fontSize="large" />}
          iconColor={themeCreator().palette.primary.main}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: "none" }}
          >
            Return
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="delegation request details tabs"
          sx={{
            minHeight: "40px",
            "& .MuiTab-root": {
              minHeight: "40px",
              py: 0.5,
              fontSize: "0.85rem",
            },
          }}
        >
          <Tab
            icon={<AssignmentIcon />}
            iconPosition="start"
            label="Request Information"
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="People Involved"
          />
          <Tab
            icon={<HistoryToggleOffRoundedIcon />}
            iconPosition="start"
            label="Messages & Timeline"
          />
        </Tabs>
      </Box>

      {/* Content container */}
      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>{renderContent()}</Box>
    </Box>
  );
};

export default DelegateRequestDetails;
